import { Link, matchPath, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowLeftToLine, faArrowRightToLine } from '@fortawesome/sharp-solid-svg-icons';
import { URLS } from 'services/urls';
import { NSButton } from 'bricks';
import { BACK_TO_PORTFOLIO_LABEL, SIDE_NAV_TEST_IDS } from './constants';
import useSideNavCollapsed from './context/SideNavCollapsedContext';
import useSideNavOpenParents from './hooks/useSideNavOpenParents';
import SideNavProjectsDealsDropdown from './components/SideNavProjectsDealsDropdown/SideNavProjectsDealsDropdown';
import PortfolioSideNav from './components/PortfolioSideNav/PortfolioSideNav';
import DealSideNav from './components/DealSideNav/DealSideNav';
import './NSNavBarV2.scss';

const NSNavBarV2 = () => {
    const { isCollapsed, toggle, setCollapsed } = useSideNavCollapsed();
    const { pathname } = useLocation();

    const dealPathMatch = matchPath({ path: '/deal/:dealId', end: false }, pathname);
    const dealId = dealPathMatch?.params.dealId;
    const isDealPage = Boolean(dealId);

    const {
        openParentIds,
        handleToggleParent,
        handleOpenParent,
    } = useSideNavOpenParents();

    const handleCollapsedParentClick = (parentId: string) => {
        setCollapsed(false);
        handleOpenParent(parentId);
    };

    const sharedNavProps = {
        isCollapsed,
        onCollapsedParentClick: handleCollapsedParentClick,
        openParentIds,
        onOpenParent: handleOpenParent,
        onToggleParent: handleToggleParent,
    };

    return (
        <nav
            className={classNames(
                'NSNavBarV2 d-flex flex-column flex-shrink-0 align-self-start',
                { 'NSNavBarV2--collapsed': isCollapsed },
            )}
            data-testid={SIDE_NAV_TEST_IDS.ROOT}
            data-collapsed={isCollapsed ? 'true' : 'false'}
            aria-label="Main navigation"
        >
            <div className="NSNavBarV2__header">
                <SideNavProjectsDealsDropdown isCollapsed={isCollapsed} dealId={dealId} />
            </div>

            {isDealPage && (
                <Link
                    to={URLS.DEALS.HOME}
                    className={classNames(
                        'd-flex align-items-center mt-2 px-2 py-1 text-muted',
                        { 'p-0 justify-content-center': isCollapsed },
                    )}
                    aria-label={BACK_TO_PORTFOLIO_LABEL}
                    title={BACK_TO_PORTFOLIO_LABEL}
                    data-testid={SIDE_NAV_TEST_IDS.BACK_TO_PORTFOLIO}
                >
                    <FontAwesomeIcon icon={faArrowLeft} fixedWidth />
                    {!isCollapsed && (
                        <span className="pl-2 font-13">{BACK_TO_PORTFOLIO_LABEL}</span>
                    )}
                </Link>
            )}

            <div className={classNames('NSNavBarV2__body flex-grow-1', { 'NSNavBarV2__body--portfolio': !isDealPage })}>
                {isDealPage ? (
                    <DealSideNav dealId={dealId!} {...sharedNavProps} />
                ) : (
                    <PortfolioSideNav {...sharedNavProps} />
                )}
            </div>

            <div className="NSNavBarV2__footer">
                <NSButton
                    className={classNames('NSNavBarV2__collapse-btn d-flex font-13', {
                        'NSNavBarV2__collapse-btn--collapsed justify-content-center w-100 px-0': isCollapsed,
                        'justify-content-start': !isCollapsed,
                    })}
                    icon={isCollapsed ? faArrowRightToLine : faArrowLeftToLine}
                    text={isCollapsed ? '' : 'Collapse'}
                    callback={toggle}
                    dataTestId={SIDE_NAV_TEST_IDS.COLLAPSE_TOGGLE}
                />
            </div>
        </nav>
    );
};

export default NSNavBarV2;
